import React, { useState, useEffect, useCallback } from "react";
import {
  fetchCommunityPosts,
  createCommunityPost,
  toggleLikePost,
  addPostComment,
  sharePost,
  getCopyLink,
  type ApiPost,
  type ApiComment,
} from "../../api/community";
import { useAuth } from "../../contexts/AuthContext";
import commingSoonImage from "../../assets/coming-soon.svg";


/* ---------- UI primitives ---------- */
const Icon = ({ name, className = "" }: { name: string; className?: string }) => (
  <span className={`material-icons ${className}`} aria-hidden>
    {name}
  </span>
);

function Avatar({ name, url }: { name: string; url?: string | null }) {
  const initials =
    (name || "U")
      .split(" ")
      .map((n) => n[0]?.toUpperCase())
      .slice(0, 2)
      .join("") || "U";
  return url ? (
    <img className="h-8 w-8 rounded-full object-cover" src={url} alt={name} />
  ) : (
    <div className="h-8 w-8 rounded-full bg-[#E6F3EA] text-[#1F2C22] grid place-items-center text-[12px] font-semibold flex-shrink-0">
      {initials}
    </div>
  );
}

function Chip({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "green" | "blue" | "orange" | "gray";
}) {
  const map: Record<string, string> = {
    default: "bg-[#F3F4F6] text-[#374151]",
    green: "bg-[#E6F3EA] text-[#1F2C22]",
    blue: "bg-[#E7F0FE] text-[#1E3A8A]",
    orange: "bg-[#FFF1DA] text-[#A15C00]",
    gray: "bg-[#EEF1F4] text-[#374151]",
  };
  return <span className={`px-2 py-[3px] text-[11.5px] rounded-full ${map[tone]}`}>{children}</span>;
}

/* ---------- view models + mappers ---------- */
type VMComment = {
  id: string | number;
  authorName: string;
  authorAvatar?: string | null;
  time: string;
  text: string;
  replies: VMComment[];
};

type VMPost = {
  id: string | number;
  authorName: string;
  authorAvatar?: string | null;
  time: string;
  text: string;
  type?: string | null;
  badge?: "new" | "win" | "question" | "art";
  likes: number;
  liked: boolean;
  comments: VMComment[];
  commentCount: number;
};

type RootUser = {
  email?: string | null;
  profile?: {
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    profile_photo?: string | null;
  } | null;
};

const getName = (u: any) =>
  u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(" ") || "Unknown";

const nameFromAny = (obj: any): string =>
  obj?.user_full_name ||
  obj?.name ||
  [obj?.first_name, obj?.last_name].filter(Boolean).join(" ") ||
  (typeof obj === "string" ? obj : "") ||
  "Unknown";

const getAvatar = (u: any) => u?.avatar_url ?? u?.profile_photo ?? null;

const mapComment = (c: ApiComment): VMComment => ({
  id: c.id,
  authorName: (c as any).user_full_name || nameFromAny(c.author || c.user),
  authorAvatar: getAvatar(c.author || c.user),
  time: new Date(c.created_at || c.timestamp || Date.now()).toLocaleString(),
  text: c.content || (c as any).text || "",
  replies: (c.replies || []).map(mapComment),
});

const mapPost = (p: ApiPost): VMPost => ({
  id: p.id,
  authorName: (p as any).user_full_name || nameFromAny(p.author || p.user),
  authorAvatar: getAvatar(p.author || p.user),
  time: new Date(p.created_at || p.timestamp || Date.now()).toLocaleString(),
  text: p.content || (p as any).text || "",
  type: p.type || null,
  badge: p.badge,
  likes: (p.likes_count ?? p.likes ?? 0) as number,
  liked: !!((p as any).has_liked ?? (p as any).is_liked ?? (p as any).liked),
  comments: (p.comments || []).map(mapComment),
  commentCount: (p as any).comments_count ?? (p.comments?.length ?? 0),
});

/* ---------- page ---------- */
export default function Community() {
  const [tab, setTab] = useState<"explore" | "news" | "my_post" | "mentions">("explore");
  const [filterType, setFilterType] = useState<string | null>(null);

  const [posts, setPosts] = useState<VMPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const { user } = useAuth();
  const [ru, setRu] = useState<RootUser | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("roott_user");
      setRu(raw ? JSON.parse(raw) : null);
    } catch {
      setRu(null);
    }
  }, []);

  const displayName =
    user?.displayName ||
    user?.email?.split("@")[0] ||
    ru?.profile?.company_name ||
    "User";

  const handleShare = async (postId: string | number) => {
    try {
      await sharePost(postId);
      const url = (await getCopyLink(postId)) || window.location.href;

      if ((navigator as any).share) {
        await (navigator as any).share({ url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setToastMsg("Link copied to clipboard");
        setTimeout(() => setToastMsg(null), 1800);
      } else {
        window.prompt("Copy link", url);
      }
    } catch (e: any) {
      setToastMsg(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to share");
      setTimeout(() => setToastMsg(null), 2200);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCommunityPosts({ category: tab, type: filterType, page: 1 });
      setPosts(data.map(mapPost));
    } catch (e: any) {
      setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to load feed");
    } finally {
      setLoading(false);
    }
  }, [tab, filterType]);

  // useEffect(() => {
    // load();
  // }, [load]);

  const submitPost = async () => {
    if (!draft.trim() || posting) return;
    try {
      setPosting(true);
      const category: "explore" | "news" | "my_post" | "mentions" = tab === "news" ? "news" : "explore";
      const type = (filterType as any) || "grant_subject";

      const temp: VMPost = {
        id: `temp_${Date.now()}`,
        authorName: "You",
        authorAvatar: undefined,
        time: "just now",
        text: draft.trim(),
        type,
        likes: 0,
        liked: false,
        comments: [],
        commentCount: 0,
      };

      setPosts((p) => [temp, ...p]);
      setDraft("");

      const created = await createCommunityPost({
        title: "",
        content: temp.text,
        category,
        type,
        mentions: [],
      });

      const real = mapPost(created);
      setPosts((p) => [real, ...p.filter((x) => x.id !== temp.id)]);
    } catch (e: any) {
      setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail||"Failed to post");
      setPosts((p) => p.filter((x) => !String(x.id).startsWith("temp_")));
    } finally {
      setPosting(false);
    }
  };

  const toggleLike = async (postId: string | number) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
      )
    );
    try {
      const res = await toggleLikePost(postId);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                liked: res?.is_liked ?? p.liked,
                likes: typeof res?.likes_count === "number" ? res.likes_count : p.likes,
              }
            : p
        )
      );
    } catch {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
        )
      );
    }
  };

  const addComment = async (postId: string | number, text: string) => {
    if (!text.trim()) return;

    const temp: VMComment = {
      id: `tc_${Date.now()}`,
      authorName: "You",
      time: "just now",
      text: text.trim(),
      replies: [],
    };

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              comments: [...p.comments, temp],
              commentCount: (p.commentCount ?? p.comments.length) + 1,
            }
          : p
      )
    );

    try {
      const created = await addPostComment(postId, text.trim());
      const mapped = mapComment(created);

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                comments: p.comments.map((c) => (c.id === temp.id ? mapped : c)),
              }
            : p
        )
      );
    } catch {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                comments: p.comments.filter((c) => c.id !== temp.id),
                commentCount: Math.max(
                  0,
                  (p.commentCount ?? p.comments.length) - 1
                ),
              }
            : p
        )
      );
    }
  };

  const addReply = async (parentId: string | number, text: string, postId: string | number) => {
    if (!text.trim()) return;
    try {
      await addPostComment(postId, text.trim());
      load();
    } catch {
    }
  };

  /* ---------- UI ---------- */
  const TYPE_CHIPS = ["grant_subject", "art", "win", "encouragement", "question", "more"] as const;
  const communityEnable=false;
  return (
    communityEnable?(<div className="mx-auto max-w-5xl px-5 xl:px-7 py-6">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-[32px] font-medium">Community Hub</h1>
        <p className="text-md text-[#777B7F]">Connect, share and learn with fellow grant writers</p>
      </div>

      {/* Composer */}
      <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm p-3 flex items-center gap-3">
        <Avatar name={displayName} />
        <div className="flex-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="w-full rounded-[8px] border border-[#E5E7EB] bg-white px-3 py-2 text-[13.5px] outline-none focus:border-[#C7CBCF] resize-none"
            placeholder="Share your thought or ask a question..."
          />
        </div>
        <button
          onClick={submitPost}
          disabled={posting || !draft.trim()}
          className="h-[36px] px-4 rounded-lg bg-primary text-sm font-medium hover:brightness-95 disabled:opacity-60"
        >
          {posting ? "Posting…" : "Post"}
        </button>
      </div>

      {/* Tabs + filter */}
      <div className="mt-3 flex items-center justify-between">
        <TabBar value={tab} onChange={setTab} />
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="text-[#6B7280]">Filter by :</span>
          <select
            className="h-8 border border-[#E5E7EB] rounded-[8px] px-2 text-[12.5px] outline-none"
            value={filterType ?? ""}
            onChange={(e) => setFilterType(e.target.value || null)}
          >
            <option value="" disabled>Select grant subject</option>
            <option value="art">art</option>
            <option value="win">win</option>
            <option value="encouragement">encouragement</option>
            <option value="question">question</option>
            <option value="more">more</option>
          </select>

          <div className="flex items-center gap-2 ml-2">
            {TYPE_CHIPS.map((t) => (
              <button
                key={t}
                onClick={() => setFilterType((f) => (f === t ? null : t))}
                className={`rounded-full px-2 py-[3px] text-[11.5px] border ${
                  filterType === t
                    ? "bg-[#1F2C22] text-white border-[#1F2C22]"
                    : "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feed */}
      <div className="mt-3 space-y-3">
        {loading && (
          <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm p-6 text-[13.5px] text-[#6B7280]">
            Loading feed…
          </div>
        )}
        {error && (
          <div className="rounded-[12px] border border-[#FCA5A5] bg-[#FEF2F2] shadow-sm p-4 text-[13px] text-[#991B1B]">
            {error}
          </div>
        )}

        {!loading &&
          !error &&
          posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onLike={() => toggleLike(p.id)}
              onComment={(text) => addComment(p.id, text)}
              onReply={(text, parentId) => addReply(parentId, text, p.id)}
              onShare={() => handleShare(p.id)}
            />
          ))}

        {toastMsg && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-black/80 text-white px-3 py-2 text-xs shadow">
            {toastMsg}
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm p-6 text-[13.5px] text-[#6B7280]">
            No posts yet.
          </div>
        )}
      </div>

      <div className="py-10" />
    </div>):(
      <div className="mx-auto px-5 xl:px-7 py-6 bg-white h-full">
          <div className="flex flex-col items-center justify-center gap-2">
            <figure className="max-w-[100%] w-full md:max-w-[360px]">
              <img src={commingSoonImage} alt="coming soon" className="w-full" />
            </figure>
            <h1 className="text-[32px] md:text-[32px] font-medium text-[#f36120]">Community</h1>
            <p className="text-md font-medium text-[#777B7F] mt-1">
              This feature is currently unavailable.
            </p>
          </div>
        </div>
    )
  );
}

function TabBar({
  value,
  onChange,
}: {
  value: "explore" | "news" | "my_post" | "mentions";
  onChange: (t: "explore" | "news" | "my_post" | "mentions") => void;
}) {
  const tabs = [
    { key: "explore", label: "Explore" },
    { key: "news", label: "News" },
    { key: "my_post", label: "My Post" },
    { key: "mentions", label: "Mentions" },
  ] as const;

  return (
    <div className="flex items-center gap-5 text-[13px]">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`pb-2 hover:text-[#111] ${
            value === t.key ? "text-[#111] font-semibold border-b-2 border-[#1F2C22]" : "text-[#6B7280]"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* Post Card */
function PostCard({
  post,
  onLike,
  onComment,
  onReply,
  onShare,
}: {
  post: VMPost;
  onLike: () => void;
  onComment: (text: string) => void;
  onReply: (text: string, parentId: string | number) => void;
  onShare: () => void;
}) {
  const [comment, setComment] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);

  const badgeMap: Record<NonNullable<VMPost["badge"]>, React.ReactNode> = {
    new: <Chip tone="blue">new</Chip>,
    win: (
      <Chip tone="orange">
        win <Icon name="celebration" className="text-[14px] align-[-1px]" />
      </Chip>
    ),
    question: <Chip tone="blue">question</Chip>,
    art: <Chip tone="gray">art</Chip>,
  };

  return (
    <div className="rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm">
      <div className="px-4 py-3 flex items-start gap-3">
        <Avatar name={post.authorName} url={post.authorAvatar} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13.5px] font-semibold">{post.authorName}</span>
            <span className="text-[12px] text-[#6B7280]">{post.time}</span>
          </div>
          <p className="mt-[6px] text-[13px] whitespace-pre-line">{post.text}</p>

          <div className="mt-2 flex items-center gap-4 text-[12.5px] text-[#6B7280]">
            <button
              onClick={onLike}
              className={`inline-flex items-center gap-1 hover:text-[#111] ${
                post.liked ? "text-[#1F2C22] font-medium" : ""
              }`}
              title="Like"
            >
              <Icon name="thumb_up" className="text-[16px]" />
              {post.likes}
            </button>

            <button
              onClick={() => setCommentsOpen((o) => !o)}
              className="inline-flex items-center gap-1 hover:text-[#111]"
            >
              <Icon name="mode_comment" className="text-[16px]" />
              Comment
              <span className="text-[12px] text-[#4B5563]">
                {post.commentCount ?? 0}
              </span>
            </button>

            <button onClick={onShare} className="inline-flex items-center gap-1 hover:text-[#111]">
              <Icon name="share" className="text-[16px]" />
              Share
            </button>
          </div>
        </div>
        <div className="pt-1">{post.badge && badgeMap[post.badge]}</div>
      </div>

      <div className="h-[1px] bg-[#EEF1F4]" />
      {commentsOpen && (
        <div className="ml-6 px-6 py-3">
          <div className="space-y-3">
            {post.comments.map((c) => (
              <Comment key={c.id} comment={c} onReply={(text) => onReply(text, c.id)} />
            ))}
          </div>

          {/* add comment */}
          <div className="mt-3 flex items-start gap-2">
            <div className="h-8 w-8 rounded-full border grid place-items-center">
              <Icon name="person" className="text-[18px] text-[#6B7280]" />
            </div>
            <div className="flex-1">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write comment..."
                className="w-full h-[36px] rounded-[8px] border border-[#E5E7EB] px-3 text-[13px] outline-none focus:border-[#C7CBCF]"
              />
            </div>
            <button
              onClick={() => {
                if (!comment.trim()) return;
                if (!commentsOpen) setCommentsOpen(true);
                onComment(comment);
                setComment("");
              }}
              className="h-[32px] px-3 rounded-[8px] bg-[#7CC68D] text-[#0F172A] text-[12.5px] font-medium hover:brightness-95"
            >
              Comment
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Threaded comment */
function Comment({
  comment,
  depth = 0,
  onReply,
}: {
  comment: VMComment;
  depth?: number;
  onReply: (text: string) => void;
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [text, setText] = useState("");

  return (
    <div className={`${depth ? "ml-10" : ""}`}>
      <div className="flex items-start gap-2">
        <Avatar name={comment.authorName} url={comment.authorAvatar} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold">{comment.authorName}</span>
            <span className="text-[12px] text-[#6B7280]">{comment.time}</span>
          </div>
          <div className="mt-[2px] text-[13px] whitespace-pre-line">{comment.text}</div>
          <button
            onClick={() => setReplyOpen((s) => !s)}
            className="mt-1 text-[12px] text-[#6B7280] hover:text-[#111]"
          >
            Reply
          </button>
        </div>
      </div>

      {replyOpen && (
        <div className="mt-2 ml-10 flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a reply…"
            className="flex-1 h-[32px] rounded-[8px] border border-[#E5E7EB] px-3 text-[13px] outline-none focus:border-[#C7CBCF]"
          />
          <button
            onClick={() => {
              if (!text.trim()) return;
              onReply(text);
              setText("");
              setReplyOpen(false);
            }}
            className="h-[28px] px-3 rounded-[8px] border border-[#E5E7EB] text-[12.5px] hover:bg-black/[0.03]"
          >
            Reply
          </button>
        </div>
      )}

      {(comment.replies || []).map((r) => (
        <div key={r.id} className="mt-3">
          <Comment comment={r} depth={depth + 1} onReply={onReply} />
        </div>
      ))}
    </div>
  );
}
